import { ensureNotNil, isNotNil } from "client/Misc/utils";
import { DimensionToRender, TileConditionalAccess, TileMaintenanceWindow } from "../RenderState/common";
import { InteractionState } from "./TileContext";
import { never } from "client_legacy/ndaw/errors";
import { useMemo } from "react";
import { useTileTooltip } from "../useTileTooltip";

type BaseLiveTileOverlayOptions = {
	interactionState: InteractionState;
	dimension: DimensionToRender;
	shouldNotHideContent?: boolean;
	appIsNotAccessible: boolean;
	appIsLicensed: boolean;
};

export type UseLiveTileOverlayOptions = BaseLiveTileOverlayOptions
	& ({
		conditionalAccess: TileConditionalAccess | nil;
		maintenanceWindow: TileMaintenanceWindow | nil;
		navigationHandler: () => void;
	} | {
		conditionalAccess?: undefined;
		maintenanceWindow?: undefined;
		navigationHandler?: undefined;
	});

export type LiveTileOverlayState = {
	isVisible: false;
} | {
	isVisible: true;
	isDisabled: boolean;
	hasOneCellHeight: boolean;
	shouldShowMenuButton: boolean;
	onClick?: () => void;
	shouldNotHideContent: boolean;
	tooltip: string | undefined;
};

export function useLiveTileOverlayState(
	options: UseLiveTileOverlayOptions
): LiveTileOverlayState {
	const {
		interactionState,
		dimension,
		conditionalAccess,
		maintenanceWindow,
		navigationHandler,
		shouldNotHideContent = false,
		appIsLicensed,
		appIsNotAccessible
	} = options;

	const tooltip: string | undefined = useTileTooltip(appIsNotAccessible, appIsLicensed);

	const isUnavailable: boolean = isNotNil(conditionalAccess)
		|| isNotNil(maintenanceWindow);

	const hasOneCellHeight: boolean = dimension.dimension.rowSpan === 1;

	return useMemo(
		(): LiveTileOverlayState => {
			switch (interactionState) {
				case InteractionState.Default:
					if (isUnavailable) {
						return {
							isVisible: true,
							isDisabled: true,
							hasOneCellHeight,
							onClick: ensureNotNil(navigationHandler),
							shouldShowMenuButton: false,
							shouldNotHideContent,
							tooltip: undefined
						};
					}

					return {
						isVisible: false
					};
				case InteractionState.Selectable:
				case InteractionState.Disabled:
					return {
						isVisible: true,
						isDisabled: interactionState === InteractionState.Disabled,
						hasOneCellHeight: hasOneCellHeight,
						shouldShowMenuButton: interactionState === InteractionState.Selectable,
						shouldNotHideContent,
						tooltip
					};
				default:
					never(interactionState);
			}
		},
		[
			hasOneCellHeight,
			interactionState,
			isUnavailable,
			navigationHandler,
			shouldNotHideContent,
			tooltip
		]
	);
}